import { motion } from "framer-motion";
import { ShieldCheck, Camera, Truck, HeartHandshake, Phone } from "lucide-react";
import { CONTACT, GUARANTEES, LUKE_IMG } from "../data";

const ICONS = { ShieldCheck, Camera, Truck, HeartHandshake };

export const MeetLuke = () => {
  return (
    <section id="meet-luke" data-testid="meet-luke" className="relative overflow-hidden carbon px-5 py-28 md:px-10 md:py-40">
      <div className="absolute left-1/2 top-0 h-[1px] w-[70%] -translate-x-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="relative overflow-hidden rounded-3xl border border-white/12">
            <img src={LUKE_IMG} alt={`${CONTACT.ownerFull} — FTC Automotive`} className="w-full object-cover" data-testid="meet-luke-photo" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="font-display text-2xl chrome-text">{CONTACT.ownerFull}</div>
              <div className="mt-1 font-accent text-[11px] uppercase tracking-[0.25em] text-[#ffb000]">
                IMI Qualified Master Mechanic · 30 Years
              </div>
            </div>
          </div>
          <div className="pointer-events-none absolute -inset-3 -z-10 rounded-[2rem] bg-[#ffb000]/10 blur-3xl" />
        </motion.div>

        <div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-accent text-xs uppercase tracking-[0.4em] text-[#ffb000]">Meet Luke</span>
            <h2 className="mt-4 font-display text-4xl font-700 uppercase leading-[0.95] chrome-text md:text-5xl">
              Your Car's In Safe Hands
            </h2>
            <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-neutral-400">
              No intimidating garage counter, no baffling jargon. Luke's the kind of mechanic who talks you
              through the job over a brew, shows you the worn part in his hand, and never charges a penny
              more than the quote. That's why every single review is five stars.
            </p>
          </motion.div>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {GUARANTEES.map((g, i) => {
              const Icon = ICONS[g.icon];
              return (
                <motion.div
                  key={g.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`guarantee-card-${i}`}
                  className="chrome-inset rounded-2xl border border-white/12 bg-white/[0.04] p-6 backdrop-blur-2xl transition-colors duration-300 hover:border-[#ffb000]/40"
                >
                  <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#ffb000]/15 text-[#ffb000]">
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <h3 className="mt-4 font-accent text-base font-600 uppercase tracking-wide text-white">{g.title}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-neutral-400">{g.copy}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.a
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            href={CONTACT.phoneHref}
            data-testid="meet-luke-call"
            className="mt-10 inline-flex items-center gap-3 rounded-full border border-[#ffb000]/40 bg-[#ffb000]/10 px-7 py-4 font-accent text-sm uppercase tracking-[0.2em] text-[#ffb000] transition-colors duration-300 hover:bg-[#ffb000] hover:text-black"
          >
            <Phone className="h-4 w-4" strokeWidth={2.4} />
            Fancy a chat first? Ring {CONTACT.owner} — {CONTACT.phone}
          </motion.a>
        </div>
      </div>
    </section>
  );
};
